


































import {
  ref, computed, useContext, useRoute, defineComponent, useFetch, onMounted,
} from '@nuxtjs/composition-api';
import { merge } from 'lodash-es';
import { useCache, CacheTagPrefix } from '@vue-storefront/cache';
import { SfBreadcrumbs, SfLoader } from '@storefront-ui/vue';
import { getBreadcrumbs, getProductProductsBlocks } from '~/modules/catalog/product/getters/productGetters';
import { useProduct } from '~/modules/catalog/product/composables/useProduct';
import { getMetaInfo } from '~/helpers/getMetaInfo';
import { usePageStore } from '~/stores/page';
import { ProductTypeEnum } from '~/modules/catalog/product/enums/ProductTypeEnum';
import { useWishlist, useApi } from '~/composables';
import LoadWhenVisible from '~/components/utils/LoadWhenVisible.vue';
import type { Product } from '~/modules/catalog/product/types';
import type { ProductDetailsQuery, ProductsBlock } from '~/modules/GraphQL/types';
import ProductSkeleton from '~/modules/catalog/product/components/ProductSkeleton.vue';
import getProductPriceBySkuGql from '~/modules/catalog/product/queries/getProductPriceBySku.gql';
import { useYoastSeo } from '~/modules/yoastSeo/composables/useYoastSeo';
import ProductsSlider from '~/modules/catalog/category/components/views/ProductsSlider.vue';
import ProductsSliderBySku from '~/modules/catalog/category/components/views/ProductsSliderBySku.vue';
import RecentlyViewedProducts from '~/modules/catalog/category/components/views/RecentlyViewedProducts.vue';
import useRecentlyViewedProducts from '~/modules/catalog/product/composables/useRecentlyViewedProducts';
import { useProductsBlocks } from '~/modules/catalog/product/composables/useProductsBlocks';
import { parseConfigurationFromHash } from '~/modules/catalog/product/helpers/configurableProduct';

export default defineComponent({
  name: 'ProductPage',
  components: {
    SfBreadcrumbs,
    SfLoader,
    SimpleProduct: () => import('~/modules/catalog/product/components/product-types/simple/SimpleProduct.vue'),
    VirtualProduct: () => import('~/modules/catalog/product/components/product-types/virtual/VirtualProduct.vue'),
    BundleProduct: () => import('~/modules/catalog/product/components/product-types/bundle/BundleProduct.vue'),
    ConfigurableProduct: () => import('~/modules/catalog/product/components/product-types/configurable/ConfigurableProduct.vue'),
    GroupedProduct: () => import('~/modules/catalog/product/components/product-types/grouped/GroupedProduct.vue'),
    LoadWhenVisible,
    ProductSkeleton,
    ProductsSlider,
    ProductsSliderBySku,
    RecentlyViewedProducts,
  },
  transition: 'fade',
  setup() {
    const { routeData } = usePageStore();
    const { query } = useApi();
    const product = ref<Product | null>(null);
    const { addTags } = useCache();
    const route = useRoute();
    const { getProductDetails, loading } = useProduct();
    const { loadProductMetaWithLdJson } = useYoastSeo();
    const { error: nuxtError } = useContext();
    const { loadWithAllItems: loadWishlist } = useWishlist();
    const { addViewedProduct } = useRecentlyViewedProducts();
    const { search: searchProductBlocks } = useProductsBlocks();
    const blocks = ref<ProductsBlock[]>([]);

    const breadcrumbs = computed(() => getBreadcrumbs(product.value).map((breadcrumb) => ({ ...breadcrumb, link: breadcrumb.link })));

    const getBaseSearchQuery = () => ({
      filter: {
        sku: {
          eq: routeData.sku,
        },
      },
      configurations: Object.values(parseConfigurationFromHash(route.value.hash, product.value?.configurable_options)) as string[],
    });

    // eslint-disable-next-line no-underscore-dangle
    const renderer = computed(() => product.value?.__typename ?? ProductTypeEnum.SIMPLE_PRODUCT);

    const fetchProductBaseData = async (searchQuery = getBaseSearchQuery()) => {
      const result = await getProductDetails({
        ...searchQuery,
      });

      product.value = merge({}, product.value, result.items[0] as Product ?? null);
      if (typeof window !== 'undefined') {
        addViewedProduct(product.value); // when fetching in browser it completes later than onMounted is called
      }
    };

    const fetchProductExtendedData = async (searchQuery = getBaseSearchQuery()) => {
      const { data } = await query<ProductDetailsQuery>(getProductPriceBySkuGql, searchQuery);
      product.value = merge({}, product.value, data.products?.items?.[0] as Product); // ! вот тут ошибка

      const attributesForUpdates = ['price_tiers'];
      for (const attributeName of attributesForUpdates) {
        const oldAttributeData = product.value?.configurable_product_options_selection?.variant?.[attributeName];
        const newAttributeData = data.products?.items?.[0]?.['configurable_product_options_selection']?.variant?.[attributeName];
        if (oldAttributeData && newAttributeData) {
          product.value.configurable_product_options_selection.variant[attributeName] = newAttributeData; // .['configurable_product_options_selection']?.variant?.[attributeName]
        }
      }
    };

    useFetch(async () => {
      await fetchProductBaseData();

      if (Boolean(product?.value?.sku) === false) nuxtError({ statusCode: 404 });

      const tags = [
        {
          prefix: CacheTagPrefix.View,
          value: `product-${routeData.sku}`,
        },
      ];

      const productTags = [{
        prefix: CacheTagPrefix.Product,
        value: product.value.uid,
      }];

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      addTags([...tags, ...productTags]);

      blocks.value = [];
      const productBlocksIdentifiers = getProductProductsBlocks(product.value);
      if (productBlocksIdentifiers.length > 0) {
        blocks.value = await searchProductBlocks(productBlocksIdentifiers);
      }
    });

    const customMeta = ref(null);
    const ldJson = ref(null);
    useFetch(async () => {
      const result = await loadProductMetaWithLdJson('catalog_product', 'sku', routeData.sku);
      customMeta.value = result.meta;
      ldJson.value = result.ldJson;
    });

    onMounted(async () => {
      await Promise.all([fetchProductExtendedData(), loadWishlist()]);
      if (product.value) {
        addViewedProduct(product.value);
      }
    });

    return {
      renderer,
      loading,
      breadcrumbs,
      product,
      fetchProduct: fetchProductExtendedData,
      customMeta,
      ldJson,
      blocks,
    };
  },
  head() {
    const metaInfo = getMetaInfo(this.product);
    if (metaInfo && this.customMeta) {
      metaInfo.meta.push(...this.customMeta);
    }
    return metaInfo;
  },
});
